import React, { useState, useEffect, useCallback } from 'react';
import { auditAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Search, 
  Shield, 
  Download, 
  Calendar,
  Activity,
  Users,
  FileText,
  TrendingUp,
  RefreshCw,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const ACTION_COLORS = {
  USER_REGISTERED: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  USER_LOGIN: 'bg-indigo-500/15 text-indigo-400',
  MEDIC_CREATED: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  MEDIC_UPDATED: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  MEDIC_DELETED: 'bg-red-500/15 text-red-400',
  SHIFT_CREATED: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  SHIFT_UPDATED: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  SHIFT_DELETED: 'bg-red-500/15 text-red-400',
  SHIFT_ASSIGNED: 'bg-indigo-500/15 text-indigo-400',
  SHIFTS_BULK_CREATED: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  SHIFTS_BULK_ASSIGNED: 'bg-indigo-500/15 text-indigo-400',
  EXPENSE_SUBMITTED: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  EXPENSE_REVIEWED: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  DOCUMENT_UPLOADED: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  DOCUMENT_APPROVED: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  DOCUMENT_REJECTED: 'bg-red-500/15 text-red-400',
  AVAILABILITY_UPDATED: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  DATA_EXPORTED: 'bg-indigo-500/15 text-indigo-400',
  AUDIT_LOGS_EXPORTED: 'bg-indigo-500/15 text-indigo-400',
  COMPLIANCE_WARNING_VIEWED: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  COMPLIANCE_WARNING_ACKNOWLEDGED: 'bg-red-500/15 text-red-400',
  COMPLIANCE_WARNING_CANCELLED: 'bg-gray-500/15 text-gray-400',
  JOB_TYPE_ELIGIBILITY_UPDATED: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  default: 'bg-gray-500/15 text-gray-400',
};

const QUICK_FILTERS = [
  { label: 'Today', days: 0 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
];

export default function AuditLogs() {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 500 };
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      if (actionFilter !== 'all') params.action = actionFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await auditAPI.list(params);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [entityFilter, actionFilter, startDate, endDate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await auditAPI.getStats(30);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleQuickFilter = (days) => {
    setActiveQuickFilter(days);
    if (days === 0) {
      const today = format(new Date(), 'yyyy-MM-dd');
      setStartDate(today);
      setEndDate(today);
    } else {
      setStartDate(format(subDays(new Date(), days), 'yyyy-MM-dd'));
      setEndDate(format(new Date(), 'yyyy-MM-dd'));
    }
  };

  const clearFilters = () => {
    setEntityFilter('all');
    setActionFilter('all');
    setStartDate('');
    setEndDate('');
    setActiveQuickFilter(null);
    setSearchQuery('');
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {};
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      if (actionFilter !== 'all') params.action = actionFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const url = auditAPI.exportUrl(params);
      
      // Use fetch with credentials to download via cookie auth
      const response = await fetch(url, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `audit_logs_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      
      toast.success('Export completed');
    } catch (error) {
      toast.error('Failed to export logs');
    } finally {
      setExporting(false);
    }
  };

  const viewLogDetail = async (log) => {
    try {
      const response = await auditAPI.getDetail(log.id);
      setSelectedLog(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load log details');
    }
  };

  const filteredLogs = logs.filter(log => 
    log.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.action?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.entity_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const uniqueActions = [...new Set(logs.map(log => log.action))].sort();

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-logs-container">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                  <Activity className="w-5 h-5 text-[#1ABC9C]" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Total Actions</p>
                  <p className="text-xl font-bold text-[#F2F2F2]">{stats.total_logs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-500/15">
                  <Users className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Active Users</p>
                  <p className="text-xl font-bold text-[#F2F2F2]">{stats.top_users?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#FFC04D]/15">
                  <FileText className="w-5 h-5 text-[#FFC04D]" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Action Types</p>
                  <p className="text-xl font-bold text-[#F2F2F2]">
                    {Object.keys(stats.action_breakdown || {}).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/15">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Period</p>
                  <p className="text-xl font-bold text-[#F2F2F2]">{stats.period_days} days</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Users & Action Breakdown */}
      {stats && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#A1A1AA]">Most Active Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.top_users?.map((user, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-[#F2F2F2]">{user.name}</span>
                    <Badge variant="outline" className="border-white/20 text-[#A1A1AA]">
                      {user.count} actions
                    </Badge>
                  </div>
                ))}
                {(!stats.top_users || stats.top_users.length === 0) && (
                  <p className="text-[#A1A1AA] text-sm text-center py-4">No activity data</p>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#A1A1AA]">Action Type Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {Object.entries(stats.action_breakdown || {})
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([action, count]) => (
                    <div key={action} className="flex items-center justify-between py-1">
                      <Badge className={`${ACTION_COLORS[action] || ACTION_COLORS.default} border-0 text-xs`}>
                        {action.replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-[#A1A1AA] text-sm font-['JetBrains_Mono']">{count}</span>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Quick Filters */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#A1A1AA]">Quick:</span>
              {QUICK_FILTERS.map(({ label, days }) => (
                <Button
                  key={days}
                  size="sm"
                  variant={activeQuickFilter === days ? "default" : "outline"}
                  onClick={() => handleQuickFilter(days)}
                  className={activeQuickFilter === days 
                    ? "bg-[#1ABC9C] hover:bg-[#1ABC9C]/90" 
                    : "border-white/10 text-[#A1A1AA]"
                  }
                >
                  {label}
                </Button>
              ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={clearFilters}
                className="text-[#A1A1AA] ml-2"
              >
                Clear All
              </Button>
            </div>

            {/* Main Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                <Input
                  placeholder="Search logs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#1A1A1A] border-white/10"
                  data-testid="search-audit-input"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#A1A1AA]" />
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setActiveQuickFilter(null); }}
                  className="bg-[#1A1A1A] border-white/10 w-[140px]"
                  placeholder="Start date"
                />
                <span className="text-[#A1A1AA]">to</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setActiveQuickFilter(null); }}
                  className="bg-[#1A1A1A] border-white/10 w-[140px]"
                  placeholder="End date"
                />
              </div>

              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="w-[150px] bg-[#1A1A1A] border-white/10">
                  <SelectValue placeholder="Entity Type" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="medic">Medic</SelectItem>
                  <SelectItem value="shift">Shift</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="availability">Availability</SelectItem>
                  <SelectItem value="template">Template</SelectItem>
                </SelectContent>
              </Select>

              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-[200px] bg-[#1A1A1A] border-white/10">
                  <SelectValue placeholder="Action Type" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10 max-h-[300px]">
                  <SelectItem value="all">All Actions</SelectItem>
                  {uniqueActions.map(action => (
                    <SelectItem key={action} value={action}>
                      {action.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex gap-2 ml-auto">
                <Button
                  variant="outline"
                  onClick={fetchLogs}
                  className="border-white/10"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <Button
                  onClick={handleExport}
                  disabled={exporting}
                  className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                  data-testid="export-audit-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#A1A1AA]">
          Showing {filteredLogs.length} of {logs.length} logs
        </p>
      </div>

      {/* Logs Table */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#A1A1AA]">Timestamp</TableHead>
                <TableHead className="text-[#A1A1AA]">User</TableHead>
                <TableHead className="text-[#A1A1AA]">Action</TableHead>
                <TableHead className="text-[#A1A1AA]">Entity</TableHead>
                <TableHead className="text-[#A1A1AA]">Details</TableHead>
                <TableHead className="text-[#A1A1AA] w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-[#A1A1AA] py-8">
                    <div className="flex flex-col items-center gap-2">
                      <Shield className="w-8 h-8 text-[#A1A1AA]/50" />
                      <span>No audit logs found</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <TableRow 
                    key={log.id} 
                    className="border-white/5 hover:bg-white/5 cursor-pointer"
                    onClick={() => viewLogDetail(log)}
                    data-testid={`audit-log-${log.id}`}
                  >
                    <TableCell className="text-[#A1A1AA] font-['JetBrains_Mono'] text-sm">
                      {format(new Date(log.timestamp), 'MMM dd, HH:mm:ss')}
                    </TableCell>
                    <TableCell className="text-[#F2F2F2]">
                      {log.user_name}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${ACTION_COLORS[log.action] || ACTION_COLORS.default} border-0`}>
                        {log.action.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[#A1A1AA]">
                      <span className="capitalize">{log.entity_type}</span>
                      {log.entity_id && (
                        <span className="text-xs ml-2 font-['JetBrains_Mono'] text-[#A1A1AA]/60">
                          #{log.entity_id.slice(0, 8)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-[#A1A1AA] max-w-[250px]">
                      <div className="text-xs font-['JetBrains_Mono'] truncate">
                        {Object.keys(log.details || {}).length > 0 
                          ? Object.entries(log.details).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(', ')
                          : '-'
                        }
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="text-[#A1A1AA]">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Log Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#1ABC9C]" />
              Audit Log Details
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Full details of this audit log entry
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-[#1A1A1A] rounded-lg space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-[#A1A1AA]">Timestamp</span>
                  <span className="text-[#F2F2F2] font-['JetBrains_Mono']">
                    {format(new Date(selectedLog.timestamp), 'yyyy-MM-dd HH:mm:ss')}
                  </span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-[#A1A1AA]">User</span>
                  <span className="text-[#F2F2F2]">{selectedLog.user_name}</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-[#A1A1AA]">Action</span>
                  <Badge className={`${ACTION_COLORS[selectedLog.action] || ACTION_COLORS.default} border-0`}>
                    {selectedLog.action.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-[#A1A1AA]">Entity Type</span>
                  <span className="text-[#F2F2F2] capitalize">{selectedLog.entity_type}</span>
                </div>
                {selectedLog.entity_id && (
                  <div className="flex justify-between items-start">
                    <span className="text-[#A1A1AA]">Entity ID</span>
                    <span className="text-[#F2F2F2] font-['JetBrains_Mono'] text-sm">
                      {selectedLog.entity_id}
                    </span>
                  </div>
                )}
              </div>

              {Object.keys(selectedLog.details || {}).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-[#A1A1AA]">Details</h4>
                  <div className="p-4 bg-[#1A1A1A] rounded-lg">
                    <pre className="text-sm font-['JetBrains_Mono'] text-[#F2F2F2] whitespace-pre-wrap break-all">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              <div className="pt-2 border-t border-white/10">
                <p className="text-xs text-[#A1A1AA] font-['JetBrains_Mono']">
                  Log ID: {selectedLog.id}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
